/**
 * IANA timezone arithmetic, done through `Intl` so DST is the platform's
 * problem rather than ours.
 *
 * Every function returns `null` for a zone the runtime does not recognise —
 * store records carry free-text timezones, and one bad row must not take the
 * whole timeline down.
 */

const WEEKDAYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

function partsIn(timeZone: string, at: Date): Record<string, number> | null {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).formatToParts(at);

    const out: Record<string, number> = {};
    for (const part of parts) {
      if (part.type !== "literal") out[part.type] = Number(part.value);
    }
    return Number.isNaN(out.year) ? null : out;
  } catch {
    return null;
  }
}

/**
 * A zone's UTC offset at a given instant, in minutes, east of UTC positive.
 * `Europe/London` in July is +60; `America/New_York` in July is -240.
 */
export function offsetMinutes(timeZone: string, at: Date): number | null {
  const parts = partsIn(timeZone, at);
  if (!parts) return null;

  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );
  // `at` is truncated to whole seconds so the difference is a clean offset.
  const instant = Math.floor(at.getTime() / 1000) * 1000;
  return Math.round((asUtc - instant) / 60000);
}

/** Minutes since local midnight in a zone. 0 at 00:00, 810 at 13:30. */
export function minutesOfDayIn(timeZone: string, at: Date): number | null {
  const parts = partsIn(timeZone, at);
  if (!parts) return null;
  return parts.hour * 60 + parts.minute;
}

/** Lowercase weekday name in a zone, e.g. `"tuesday"`. */
export function weekdayIn(timeZone: string, at: Date): string | null {
  try {
    // `en-US` + `weekday: long` is stable across runtimes; deriving the index
    // from a UTC-shifted Date would be wrong across the date line.
    const name = new Intl.DateTimeFormat("en-US", {
      timeZone,
      weekday: "long",
    }).format(at);
    const key = name.toLowerCase();
    return WEEKDAYS.includes(key) ? key : null;
  } catch {
    return null;
  }
}

/**
 * Minutes to add to a store-local clock reading to get the equivalent reading
 * on `viewerZone`'s clock. `null` if either zone is unusable.
 */
export function shiftBetween(
  storeZone: string,
  viewerZone: string,
  at: Date,
): number | null {
  const store = offsetMinutes(storeZone, at);
  const viewer = offsetMinutes(viewerZone, at);
  if (store === null || viewer === null) return null;
  return viewer - store;
}

/** The browser's zone, or UTC if the runtime will not say. */
export function viewerTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/** Whether the runtime accepts a zone name, so callers can reject bad input. */
export function isValidTimeZone(timeZone: string): boolean {
  return offsetMinutes(timeZone, new Date()) !== null;
}

/**
 * Every IANA zone the runtime knows.
 *
 * `Intl.supportedValuesOf` is ES2022, past this app's `lib` target, so it is
 * reached through a guarded cast. Older runtimes get an empty list and callers
 * fall back to the zones present in the data.
 */
export function allTimeZones(): string[] {
  try {
    const supportedValuesOf = (
      Intl as unknown as {
        supportedValuesOf?: (key: string) => string[];
      }
    ).supportedValuesOf;
    return typeof supportedValuesOf === "function"
      ? supportedValuesOf("timeZone")
      : [];
  } catch {
    return [];
  }
}

/** `"Asia/Kathmandu"` → `"UTC+05:45"`. Empty string for an unusable zone. */
export function offsetLabel(timeZone: string, at: Date): string {
  const offset = offsetMinutes(timeZone, at);
  if (offset === null) return "";
  const sign = offset < 0 ? "-" : "+";
  const abs = Math.abs(offset);
  const hours = String(Math.floor(abs / 60)).padStart(2, "0");
  const mins = String(abs % 60).padStart(2, "0");
  return `UTC${sign}${hours}:${mins}`;
}

/** `810` → `"13:30"`. Wraps, so 1470 renders as `"00:30"`. */
export function formatMinutes(minutes: number): string {
  const wrapped = ((Math.round(minutes) % 1440) + 1440) % 1440;
  const hours = Math.floor(wrapped / 60);
  const mins = wrapped % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
}
